import { Injectable, inject } from '@angular/core';
import { FilingFormState } from './filing-form-state.service';
import { MappingStatusService } from './mapping-status.service';
import { CrossReferenceService } from './cross-reference.service';
import { buildRoleCodeToStateKey } from '../../models/sheet-descriptors';
import { environment } from '../../../environments/environment';
import {
  SummableControl,
  isSummableMonetaryTotal,
  extractedSumWarningEnabled,
  compareColumns,
  describeBaselineColumns,
  BaselineColumnStatus,
} from './extracted-sum-warning';

/** Marker substring used by every sheet to identify (and replace) these warnings. */
export const EXTRACTED_SUM_MARKER = 'does not match the extracted sum';

/**
 * Role-code -> stateKey, for resolving a control's `ui.linkTo.role` to the
 * sheet whose N8N snapshot holds the inherited field's extracted value. Derived
 * once from the SHEET_DESCRIPTORS single source.
 */
const ROLE_CODE_TO_STATE_KEY = buildRoleCodeToStateKey();

export interface MismatchWarningInput {
  sheetKey: keyof FilingFormState;
  controls: readonly SummableControl[];
  /** Visible columns the sheet decides to check. `key` is opaque to the service. */
  columns: { key: string; label: string }[];
  getControlName: (qname: string, columnKey: string) => string;
  getCurrentValue: (controlName: string) => unknown;
}

/**
 * Single authority for the "Suggested Change / extracted-sum mismatch" warning.
 * Mirrors MappingStatusService's pure-module + thin-service split. Each sheet
 * supplies its own column layout + control-name builder; this service owns the
 * predicate, the snapshot read, the comparison and the message text.
 */
@Injectable({ providedIn: 'root' })
export class ExtractedSumWarningService {
  private mappingStatus = inject(MappingStatusService);
  private crossRef = inject(CrossReferenceService);

  /**
   * Returns a map of control qname -> warning messages (one per mismatching
   * visible column).
   *
   * Baseline source per control:
   *   - **Plain total** — the host sheet's own N8N snapshot, keyed by the
   *     control's own qname (the original behaviour).
   *   - **linkTo leaf** (`ui.linkTo`) — the LINK TARGET sheet's snapshot, keyed
   *     by `linkTo.qname`. The displayed value is inherited from the target, so
   *     its authoritative extracted value lives on the target, not the host's
   *     own (stale) snapshot. Sourcing the host snapshot here raised a false
   *     "does not match the extracted sum of RM<stale>" for a value that ties
   *     out against its real origin (PO, 2026-06-19).
   *
   * A control whose baseline source is empty/absent yields no warning (the
   * pre-rollout short-circuit, now applied per-control so a linkTo leaf can warn
   * from its target even when the host snapshot is absent).
   */
  computeMismatchWarnings(input: MismatchWarningInput): Map<string, string[]> {
    const result = new Map<string, string[]>();
    const hostBaseline = this.mappingStatus.getSnapshotValues(input.sheetKey);

    for (const control of input.controls) {
      if (!isSummableMonetaryTotal(control)) continue;
      if (!extractedSumWarningEnabled(control)) continue;

      // Resolve the baseline source: the link target ONLY for a value-inherited
      // leaf, else the host's own snapshot.
      //
      // A `linkTo` is value-inheritance only when the control does NOT compute its
      // own value (`calculated !== true`) — e.g. SOFP `LongtermBorrowings`
      // (`calculated:false`, pulled from role 9). A CALCULATED total may also carry
      // a `linkTo` that is NAVIGATION-only (e.g. role 12 `ProfitLoss` → 410000,
      // `calculated:true`): it computes its own value and its authoritative
      // extracted baseline is the HOST sheet's own snapshot. Redirecting such a
      // total to its nav target suppressed its legitimate warning while the pure
      // calc-diff still flagged the host snapshot → a phantom Success* with no
      // in-sheet warning. Gate the redirect on `calculated !== true`.
      const link = control.ui?.linkTo;
      const isValueInherited = control.ui?.calculated !== true;
      let baselineValues: Record<string, unknown>;
      let baselineControlQname: string;
      if (link?.role && link?.qname && isValueInherited) {
        const targetKey = ROLE_CODE_TO_STATE_KEY[link.role];
        baselineValues = targetKey
          ? this.mappingStatus.getSnapshotValues(targetKey)
          : {};
        baselineControlQname = link.qname;
      } else {
        baselineValues = hostBaseline ?? {};
        baselineControlQname = control.qname;
      }
      if (!baselineValues || Object.keys(baselineValues).length === 0) continue;

      const mismatches = compareColumns({
        controlQname: control.qname,
        baselineControlQname,
        columns: input.columns,
        getControlName: input.getControlName,
        getCurrentValue: input.getCurrentValue,
        getBaselineValue: (name) => baselineValues[name],
      });

      if (mismatches.length > 0) {
        result.set(
          control.qname,
          mismatches.map((mismatch) =>
            this.crossRef.generateMismatchError(mismatch.baseline, mismatch.current, mismatch.label)
          )
        );
      }
    }
    return result;
  }

  /**
   * Uniform merge: replace any prior extracted-sum warnings in `validationErrors`
   * with the freshly computed ones, preserving all other (red) error types.
   * Replaces each sheet's hand-rolled per-control merge block.
   */
  mergeIntoValidationErrors(
    validationErrors: Map<string, string[]>,
    controls: readonly SummableControl[],
    warnings: Map<string, string[]>
  ): void {
    for (const control of controls) {
      const existing = validationErrors.get(control.qname) || [];
      const nonApi = existing.filter((message) => !message.includes(EXTRACTED_SUM_MARKER));
      const apiMsgs = warnings.get(control.qname) || [];
      const merged = [...nonApi, ...apiMsgs];
      if (merged.length > 0) validationErrors.set(control.qname, merged);
      else validationErrors.delete(control.qname);
    }
  }

  /**
   * Debug-overlay annotation (Ctrl+Alt+B) for a single control: reports
   * whether an N8N extracted baseline is present per period (Current /
   * Previous), so devs can see why the "does not match the extracted sum"
   * advisory is or isn't able to fire on this row. The dimension
   * (Consolidated / Separate / Group / Company) is already shown by the
   * sheet's badge + column headers, so it is collapsed away here. Returns ''
   * unless the control passes the same Gate 1 (summable monetary total) and
   * Gate 2 (warning not opted out) the real warning uses, and is skipped for
   * value-inherited (linkTo) fields. Never renders in production.
   *
   * Output (HTML, period-collapsed):
   *   <span class="baseline-debug">[N8N BASELINE Extracted]: Current ✓ · Previous ✗</span>
   */
  baselineDebugForControl(
    sheetKey: keyof FilingFormState,
    control: SummableControl,
    columns: { key: string; label: string }[],
    getControlName: (qname: string, columnKey: string) => string
  ): string {
    if (environment.production) return '';
    if (!isSummableMonetaryTotal(control)) return '';
    if (!extractedSumWarningEnabled(control)) return '';
    // VALUE-INHERITED (linkTo) fields pull their value from an origin sum field
    // on another sheet; the origin already carries this overlay, so suppress the
    // linked copy.
    //
    // Gate on `calculated !== true`, mirroring computeMismatchWarnings above — a
    // CALCULATED total may also carry a linkTo that is NAVIGATION-only (role 12
    // `ProfitLoss` / `ProfitLoss_2` → 410000): it computes its own value, its
    // baseline is this sheet's own snapshot, and the real warning DOES fire on
    // it. A blanket `if (linkTo) return ''` here suppressed the overlay for
    // exactly the rows a dev is most likely to be debugging — the annotation
    // vanished while the advisory it explains still rendered.
    const link = (control.ui as { linkTo?: unknown })?.linkTo;
    if (link && control.ui?.calculated !== true) return '';

    // No empty-snapshot short-circuit (unlike computeMismatchWarnings): an
    // absent snapshot legitimately means every column is absent, and showing
    // "all ✗" IS the debug signal devs want ("no baseline was extracted").
    const baseline = this.mappingStatus.getSnapshotValues(sheetKey) ?? {};
    const statuses = describeBaselineColumns({
      controlQname: control.qname,
      columns,
      getControlName,
      getBaselineValue: (name) => baseline[name],
    });

    const parts = this.collapseByPeriod(statuses).map(
      (entry) => `${entry.label} ${entry.mark}`
    );
    return `<span class="baseline-debug">[N8N BASELINE Extracted]: ${parts.join(' · ')}</span>`;
  }

  /**
   * Collapse per-column baseline statuses to one entry per period — the token
   * of `label` after its last space, e.g. "Consolidated Current" → "Current",
   * "Group Previous" → "Previous", or the whole label when it is already a
   * bare period ("Current"). A period is ✓ if all its columns are present, ✗
   * if all absent; if they disagree it stays ✓ but names the dimension(s) that
   * ARE present, e.g. "✓ (Separate only)". First-seen period order is kept.
   */
  private collapseByPeriod(
    statuses: BaselineColumnStatus[]
  ): { label: string; mark: string }[] {
    const order: string[] = [];
    const groups = new Map<string, BaselineColumnStatus[]>();
    for (const status of statuses) {
      const lastSpace = status.label.lastIndexOf(' ');
      const period = lastSpace >= 0 ? status.label.slice(lastSpace + 1) : status.label;
      if (!groups.has(period)) {
        groups.set(period, []);
        order.push(period);
      }
      groups.get(period)!.push(status);
    }

    return order.map((period) => {
      const cols = groups.get(period)!;
      const present = cols.filter((column) => column.present);
      if (present.length === 0) return { label: period, mark: '✗' }; // none present
      if (present.length === cols.length) return { label: period, mark: '✓' }; // all present
      // Disagree within this period: name the dimension(s) that ARE present
      // (token BEFORE the last space, e.g. "Separate"). Bare-period labels have
      // no dimension token, so they fall back to a plain ✓.
      const dims = present
        .map((column) => {
          const lastSpace = column.label.lastIndexOf(' ');
          return lastSpace > 0 ? column.label.slice(0, lastSpace) : '';
        })
        .filter((dimension) => dimension);
      return { label: period, mark: dims.length ? `✓ (${dims.join('/')} only)` : '✓' };
    });
  }
}
